import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Trophy, X, Clock, Languages, CheckCircle2, XCircle, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function QuizPlayer() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const { branding } = useBranding();
  const brandColor = branding?.theme_color || "#0EA5E9";
  const [session, setSession] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]); // per-question selected index (or -1)
  const [timeLeft, setTimeLeft] = useState(0);
  const [lang, setLang] = useState("both");
  const [translations, setTranslations] = useState({});
  const [translating, setTranslating] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const startTime = useRef(Date.now());
  const tickRef = useRef(null);

  useEffect(() => {
    const cached = sessionStorage.getItem(`attempt-${attemptId}`);
    if (cached) {
      const parsed = JSON.parse(cached);
      setSession(parsed);
      setTimeLeft(parsed.questionnaire.time_per_q_sec || 30);
      setAnswers(parsed.questionnaire.questions.map(q => (q.question_type || "single") === "multi" ? [] : -1));
    } else {
      toast.error("Session expired. Please scan the QR again.");
      nav("/");
    }
    // Load available languages
    api.get("/languages").then(r => setAvailableLanguages(r.data)).catch(()=>{});
  }, [attemptId, nav]);

  // When user picks a regional language not yet cached, fetch translation
  useEffect(() => {
    const isRegional = lang && !["en", "hi", "both"].includes(lang);
    if (!isRegional || translations[lang]) return;
    let cancelled = false;
    (async () => {
      setTranslating(true);
      try {
        const r = await api.post("/public/translate-quiz", { attempt_id: attemptId, lang_code: lang });
        if (!cancelled) {
          const byId = {};
          r.data.questions.forEach(q => { byId[q.id] = q; });
          setTranslations(prev => ({ ...prev, [lang]: byId }));
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || "Translation failed");
          setLang("both");
        }
      } finally { if (!cancelled) setTranslating(false); }
    })();
    return () => { cancelled = true; };
  }, [lang, attemptId, translations]);

  // timer per question — resets when idx changes; auto-advance lock not used (we allow change), but time pressure remains.
  useEffect(() => {
    if (!session || result) return;
    setTimeLeft(session.questionnaire.time_per_q_sec || 30);
    clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(tickRef.current);
          // time up — auto-move forward, keeping whatever answer they had (or -1)
          autoAdvance();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, session]);

  if (!session) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin" style={{ color: brandColor }}/></div>;

  const qs = session.questionnaire.questions;
  const total = qs.length;
  const current = qs[idx];
  const isMulti = (current.question_type || "single") === "multi";
  const selected = answers[idx];
  const hasAnswer = isMulti ? (Array.isArray(selected) && selected.length > 0) : (selected !== -1 && selected != null);
  // Regional translation lookup
  const isRegional = lang && !["en", "hi", "both"].includes(lang);
  const trBank = isRegional ? (translations[lang] || {}) : null;
  const tr = trBank ? trBank[current.id] : null;

  function selectOption(choice) {
    const next = [...answers];
    if (isMulti) {
      const cur = new Set(Array.isArray(next[idx]) ? next[idx] : []);
      if (cur.has(choice)) cur.delete(choice); else cur.add(choice);
      next[idx] = Array.from(cur).sort((a,b)=>a-b);
    } else {
      next[idx] = choice;
    }
    setAnswers(next);
  }

  function autoAdvance() {
    // proceed to next when timer ends
    if (idx + 1 < total) setIdx(idx + 1);
    else submitAll();
  }

  async function submitAll() {
    setSubmitting(true);
    try {
      const dur = Math.floor((Date.now() - startTime.current) / 1000);
      const r = await api.post("/public/q/submit", { attempt_id: attemptId, answers, duration_sec: dur });
      setResult(r.data);
      sessionStorage.removeItem(`attempt-${attemptId}`);
      clearInterval(tickRef.current);
    } catch (e) {
      toast.error("Submission failed");
    } finally { setSubmitting(false); }
  }

  function goNext() {
    if (idx + 1 < total) setIdx(idx + 1);
    else submitAll();
  }
  function goPrev() {
    if (idx > 0) setIdx(idx - 1);
  }

  // Result screen
  if (result) {
    const pct = result.percentage;
    const tier = pct >= 85 ? { label: "Outstanding!", color: "text-green-600", emoji: "🏆" } :
                 pct >= 70 ? { label: "Great job!", color: "text-sky-600", emoji: "🌟" } :
                 pct >= 50 ? { label: "Keep practicing", color: "text-amber-600", emoji: "💪" } :
                             { label: "Time to review", color: "text-red-600", emoji: "📚" };
    return (
      <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${brandColor}10, #fff)` }}>
        <div className="max-w-md sm:max-w-xl lg:max-w-2xl mx-auto p-6 py-10 space-y-6">
          <div className="text-center pop-in">
            <div className="text-6xl">{tier.emoji}</div>
            <div className={`mt-3 font-display font-black text-3xl ${tier.color}`}>{tier.label}</div>
            <div className="text-sm text-slate-500 mt-1">{result.questionnaire_title}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-3xl p-6 text-center">
            <div className="text-xs uppercase tracking-[0.22em] font-bold" style={{ color: brandColor }}>Your Score</div>
            <div className="font-display font-black text-6xl text-slate-900 mt-1">{result.score}<span className="text-slate-400 text-3xl">/{result.max_score}</span></div>
            <div className="text-2xl text-slate-700 font-bold mt-1">{pct}%</div>
            <div className="flex justify-center gap-4 mt-5 pt-5 border-t border-slate-100">
              <div><div className="text-2xl font-bold text-green-600">{result.correct}</div><div className="text-xs text-slate-400">Correct</div></div>
              <div><div className="text-2xl font-bold text-red-500">{result.total - result.correct}</div><div className="text-xs text-slate-400">Wrong</div></div>
              <div><div className="text-2xl font-bold text-slate-700">{result.total}</div><div className="text-xs text-slate-400">Total</div></div>
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="font-display font-bold text-lg">Review</h3>
            {result.review.map((r, i) => (
              <div key={i} className={`p-3 rounded-xl border-2 ${r.is_correct ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                <div className="flex items-start gap-2">
                  {r.is_correct ? <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0"/> : <XCircle className="h-5 w-5 text-red-500 flex-shrink-0"/>}
                  <div className="text-sm flex-1">
                    <div className="font-bold">Q{i+1}: {qs[i].text_en}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{qs[i].text_hi}</div>
                    {!r.is_correct && (
                      <div className="text-xs mt-1.5 text-slate-700">
                        <span className="font-bold">Correct:</span>{" "}
                        {Array.isArray(r.correct_index)
                          ? r.correct_index.map(ci => qs[i].options_en[ci]).join(" + ")
                          : qs[i].options_en[r.correct_index]}
                      </div>
                    )}
                    {r.explanation_en && <div className="text-xs text-slate-600 mt-1.5">💡 {r.explanation_en}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={() => {
              const tk = session?.questionnaire?.token;
              const isFlow = session?.questionnaire?.assessment_flow_enabled;
              if (tk && isFlow) { nav(`/q/${tk}`); } else { nav("/"); }
            }} className="w-full h-12 rounded-2xl text-white font-bold" style={{ background: brandColor }} data-testid="result-done">
            {session?.questionnaire?.assessment_flow_enabled ? "Continue Assessment Flow" : "Done"}
          </Button>
        </div>
      </div>
    );
  }

  const answeredCount = answers.filter(a => Array.isArray(a) ? a.length > 0 : (a !== -1 && a != null)).length;
  const progressPct = (answeredCount / total) * 100;
  const isLast = idx + 1 === total;
  const banner = current.image_path ? fileUrl(current.image_path) : (current.image_url || "");

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${brandColor}10, #fff)` }}>
      {/* Header */}
      <div className="sticky top-0 z-10 glass border-b border-slate-200/60">
        <div className="max-w-md sm:max-w-xl lg:max-w-2xl mx-auto p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
          <Button size="icon" variant="ghost" onClick={() => { if(window.confirm("Exit quiz? Progress will be lost.")) nav("/"); }} className="h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0">
            <X className="h-5 w-5"/>
          </Button>
          <div className="flex-1 min-w-0">
            <Progress value={progressPct} className="h-2.5 sm:h-3 rounded-full"/>
            <div className="text-[10px] sm:text-xs text-slate-500 mt-1 font-bold truncate">Question {idx+1} of {total} · {answeredCount} answered</div>
          </div>
          <div className={`flex items-center gap-1 sm:gap-1.5 rounded-full px-2 sm:px-3 py-1 sm:py-1.5 font-bold text-xs sm:text-sm flex-shrink-0 ${timeLeft <= 5 ? "bg-red-100 text-red-700" : "bg-sky-100 text-sky-700"}`}>
            <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4"/>{timeLeft}s
          </div>
        </div>
      </div>

      <div className="max-w-md sm:max-w-xl lg:max-w-2xl mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div className="flex justify-end">
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="w-auto h-8 text-xs font-bold rounded-full bg-white border-slate-200 gap-1.5" data-testid="lang-select">
              <Languages className="h-3.5 w-3.5" style={{ color: brandColor }}/>
              <SelectValue/>
              {translating && <Loader2 className="h-3 w-3 animate-spin"/>}
            </SelectTrigger>
            <SelectContent>
              {availableLanguages.map(l => (
                <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl p-6 pop-in" key={idx}>
          {banner && (
            <div className="rounded-2xl overflow-hidden bg-slate-100 mb-4 flex items-center justify-center max-h-[45vh]">
              <img src={banner} alt="" className="max-w-full max-h-[45vh] object-contain"/>
            </div>
          )}
          <div className="text-xs uppercase tracking-[0.22em] font-bold mb-3" style={{ color: brandColor }}>Question {idx+1}</div>
          {current.html_content && !isRegional ? (
            <div className="prose prose-sm sm:prose-base max-w-none" dangerouslySetInnerHTML={{ __html: current.html_content }}/>
          ) : isRegional ? (
            tr ? (
              <h2 className="font-display font-bold text-xl text-slate-900 leading-snug">{tr.text}</h2>
            ) : (
              <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin"/>Translating…</div>
            )
          ) : (
            <>
              {(lang === "en" || lang === "both") && (
                <h2 className="font-display font-bold text-xl text-slate-900 leading-snug">{current.text_en}</h2>
              )}
              {(lang === "hi" || lang === "both") && (
                <p className={`text-slate-500 leading-relaxed ${lang === "both" ? "mt-2 text-base" : "text-xl text-slate-900 font-bold"}`}>{current.text_hi}</p>
              )}
            </>
          )}
        </div>

        <div className="space-y-3">
          {isMulti && (
            <div className="text-xs font-bold text-slate-600 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: `${brandColor}15`, color: brandColor }}>
              ✓ Multi-select · choose all that apply
            </div>
          )}
          {current.options_en.map((opt, i) => {
            const isSel = isMulti
              ? (Array.isArray(selected) && selected.includes(i))
              : selected === i;
            return (
              <button
                key={i}
                data-testid={`option-${idx}-${i}`}
                onClick={() => selectOption(i)}
                className={`tactile-btn p-4 w-full text-left ${isSel ? "selected" : ""}`}
                style={isSel ? { borderColor: brandColor, background: `${brandColor}15` } : undefined}
              >
                <div className="flex items-start gap-3">
                  <div className={`h-8 w-8 flex-shrink-0 grid place-items-center font-display font-black ${isMulti ? "rounded-md" : "rounded-full"}`}
                    style={isSel ? { background: brandColor, color: "#fff" } : { background: "#f1f5f9", color: "#475569" }}>
                    {isMulti ? (isSel ? "✓" : "") : String.fromCharCode(65+i)}
                  </div>
                  <div className="flex-1">
                    {isRegional ? (
                      <div className="font-bold text-slate-900">{tr ? tr.options[i] : current.options_en[i]}</div>
                    ) : (
                      <>
                        {(lang === "en" || lang === "both") && <div className="font-bold text-slate-900">{opt}</div>}
                        {(lang === "hi" || lang === "both") && (
                          <div className={lang === "both" ? "text-sm text-slate-500 mt-0.5" : "font-bold text-slate-900"}>{current.options_hi[i]}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="text-xs text-slate-500 italic text-center -mt-1">You can change your answer until you tap Next.</div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={goPrev} disabled={idx === 0} className="flex-1 h-14 rounded-2xl" data-testid="prev-btn">
            <ArrowLeft className="h-5 w-5 mr-1.5"/>Previous
          </Button>
          <Button onClick={goNext} disabled={submitting || !hasAnswer}
            data-testid={isLast ? "submit-btn" : "next-btn"}
            className="flex-[2] h-14 rounded-2xl text-white font-bold text-lg shadow-lg"
            style={{ background: brandColor, boxShadow: `0 10px 25px -10px ${brandColor}` }}>
            {submitting ? <Loader2 className="h-5 w-5 animate-spin"/> :
              isLast ? <><Trophy className="h-5 w-5 mr-2"/>Submit Quiz</> :
              <>Next <ArrowRight className="h-5 w-5 ml-1.5"/></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
